// Delegation — a supplier handing ONE document to a colleague who's better
// placed to complete or sign it ("the director signs this, I'm ops").
//
// Pure helpers plus the three writes the supplier side needs. The delegate's
// own path (open + submit) deliberately lives server-side in
// api/delegation-lookup.js / api/delegation-submit.js, because a delegate has
// no session — see the comment there for why they don't get one.

import { supabase } from '../supabaseClient';
import { newSecureToken } from './secureToken';

export const DELEGATION_DAYS = 14;

export const SCOPES = [
  {
    key: 'complete_and_sign',
    label: 'Complete and sign it',
    hint: 'They fill it in and sign. It goes straight to Fusion 2K for review.',
  },
  {
    key: 'complete',
    label: 'Fill it in, then send it back to me to sign',
    hint: "They can't sign — you do that once they've finished.",
  },
];

// An acknowledge document has no fields to fill in, so "complete, then send it
// back" would ask someone to do nothing. Only the sign-it scope is meaningful.
export const scopesFor = (satisfiedBy) =>
  (satisfiedBy === 'acknowledge' ? SCOPES.filter(s => s.key === 'complete_and_sign') : SCOPES);

export const scopeLabel = (key) => SCOPES.find(s => s.key === key)?.label || key;

// A delegation is finished with when it's been submitted, revoked or has run
// out of time — used to split the live list from the history.
export const isOpen = (d) =>
  d && !['submitted', 'revoked', 'expired'].includes(d.status)
  && !(d.expires_at && new Date(d.expires_at).getTime() < Date.now());

// What the supplier sees against each row. Expiry is derived rather than
// trusted from `status`, because nothing sets 'expired' until someone opens
// the link — so a row can sit at 'pending' long after it stopped working.
export function delegationState(d) {
  if (!d) return { key: 'unknown', label: 'Unknown', tone: 'grey' };
  if (d.status === 'submitted') return { key: 'submitted', label: 'Completed', tone: 'green' };
  if (d.status === 'revoked') return { key: 'revoked', label: 'Withdrawn', tone: 'grey' };
  if (d.expires_at && new Date(d.expires_at).getTime() < Date.now()) return { key: 'expired', label: 'Expired', tone: 'amber' };
  if (d.status === 'opened') return { key: 'opened', label: 'Opened, not finished', tone: 'amber' };
  return { key: 'pending', label: 'Sent, not opened', tone: 'amber' };
}

// 32 random bytes, hex. Uses the platform CSPRNG — Math.random() is not
// acceptable for something that acts as a credential for one document.
// Now delegates to lib/secureToken so the setup-form link and this one cannot
// diverge in strength again (the setup form used to have a Math.random
// fallback; see that file).
export function newDelegationToken() {
  return newSecureToken(32);
}

export const delegationUrl = (token, origin = window.location.origin) => `${origin}/d/${token}`;

/**
 * Create a delegation. Returns { delegation, url } or throws with a message
 * fit to show the supplier.
 *
 * The row is inserted by the SUPPLIER's own session (RLS `supplier_own`), so a
 * supplier can only ever delegate its own documents.
 */
export async function createDelegation({ supplierId, documentKey, invitedByUserId, name, email, role, scope }) {
  const token = newDelegationToken();
  const expiresAt = new Date(Date.now() + DELEGATION_DAYS * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('document_form_delegations')
    .insert({
      document_key: documentKey,
      supplier_id: supplierId,
      invited_by_user_id: invitedByUserId || null,
      invitee_name: name?.trim() || null,
      invitee_email: email?.trim() || null,
      invitee_role: role?.trim() || null,
      scope,
      token,
      expires_at: expiresAt,
      status: 'pending',
    })
    .select('id, token, expires_at, invitee_name, invitee_email, invitee_role, scope, status, document_key')
    .single();

  if (error) throw new Error(error.message);
  return { delegation: data, url: delegationUrl(data.token) };
}

/** Withdraw a delegation. The link stops working immediately. */
export async function revokeDelegation(id) {
  const { error } = await supabase
    .from('document_form_delegations')
    .update({ status: 'revoked', revoked_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Extend a delegation that ran out of time, rather than making someone create
 * a new one and re-type the same details. The TOKEN IS ROTATED: the old link
 * may be sitting in an inbox or a browser history, and an expiry that can be
 * undone without invalidating the old URL isn't much of an expiry.
 */
export async function reissueDelegation(id) {
  const token = newDelegationToken();
  const { data, error } = await supabase
    .from('document_form_delegations')
    .update({
      token,
      status: 'pending',
      expires_at: new Date(Date.now() + DELEGATION_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      opened_at: null,
      revoked_at: null,
    })
    .eq('id', id)
    .select('id, token, expires_at, invitee_name, invitee_email, scope, document_key')
    .single();
  if (error) throw new Error(error.message);
  return { delegation: data, url: delegationUrl(data.token) };
}

/** Every delegation on a supplier's documents, newest first. */
export async function listDelegations(supplierId) {
  const { data, error } = await supabase
    .from('document_form_delegations')
    .select('id, document_key, scope, status, token, expires_at, created_at, opened_at, submitted_at, invitee_name, invitee_email, invitee_role')
    .eq('supplier_id', supplierId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}
